import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import styles from './Carousel2.module.scss';

type Carousel2Props = {
  items: JSX.Element[];
  itemWidth?: number;
  minItemWidth?: number;
  maxItemWidth?: number;
  arrowColor?: string;
  legends?: string[];
  showLegends?: boolean;
  legendColor?: string;
  thumbnails?: JSX.Element[];
  showThumbnails?: boolean;
  thumbnailsPosition?: 'top' | 'bottom' | 'left' | 'right';
  thumbnailsPositionMobile?: 'top' | 'bottom';
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  thumbnailWidthMobile?: number;
  thumbnailHeightMobile?: number;
  loop?: boolean;
  mobileBreakpoint?: number;
  onSlideChange?: (index: number) => void; 
  initialIndex?: number;
};

const RightArrowSVG = ({ color = '#000000' }: { color?: string }) => (
  <svg
    version="1.0"
    xmlns="http://www.w3.org/2000/svg"
    width="100%"
    height="100%"
    viewBox="0 0 30 42"
    preserveAspectRatio="xMidYMid meet"
  >
    <g transform="translate(0,42) scale(0.1,-0.1)" fill={color} stroke="none">
      <path d="M43 385 c-22 -25 -15 -39 57 -110 l70 -69 -70 -71 c-38 -39 -70 -77-70 -86 0 -21 20 -39 44 -39 27 0 196 168 196 195 0 23 -177 195 -200 194 -8 0 -21 -6 -27 -14z" />
    </g>
  </svg>
);

const LeftArrowSVG = ({ color = '#000000' }: { color?: string }) => (
  <svg
    version="1.0"
    xmlns="http://www.w3.org/2000/svg"
    width="100%"
    height="100%"
    viewBox="0 0 30 42"
    preserveAspectRatio="xMidYMid meet"
  >
    <g transform="translate(0,42) scale(0.1,-0.1)" fill={color} stroke="none">
      <path d="M257 385 c22 -25 15 -39 -57 -110 l-70 -69 70 -71 c38 -39 70 -77 70 -86 0 -21 -20 -39 -44 -39 -27 0 -196 168 -196 195 0 23 177 195 200 194 8 0 21 -6 27 -14z" />
    </g>
  </svg>
);

export const Carousel2 = ({
  items,
  itemWidth,
  minItemWidth = 200,
  maxItemWidth = 550,
  arrowColor = '#000000',
  legends = [],
  showLegends = false,
  legendColor = '#000000',
  thumbnails = [],
  showThumbnails = false,
  thumbnailsPosition = 'bottom',
  thumbnailsPositionMobile = 'bottom',
  thumbnailWidth = 80,
  thumbnailHeight = 80,
  thumbnailWidthMobile = 50,
  thumbnailHeightMobile = 50,
  loop = false,
  mobileBreakpoint = 768,
  onSlideChange,
  initialIndex
}: Carousel2Props) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  const [, setVisibleItems] = useState(1);
  const [calculatedItemWidth, setCalculatedItemWidth] = useState(itemWidth || maxItemWidth);
  const [gap, setGap] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchMoveX, setTouchMoveX] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [effectiveThumbnailPosition, setEffectiveThumbnailPosition] = useState(thumbnailsPosition);
  const [effectiveThumbnailWidth, setEffectiveThumbnailWidth] = useState(thumbnailWidth);
  const [effectiveThumbnailHeight, setEffectiveThumbnailHeight] = useState(thumbnailHeight);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onSlideChange) {
      onSlideChange(currentIndex);
    }
  }, [currentIndex, onSlideChange]);

  useEffect(() => {
    const updateLayout = () => {
      if (!mainContainerRef.current) return;
      const windowWidth = window.innerWidth;
      const isMobileView = windowWidth <= mobileBreakpoint;
      setIsMobile(isMobileView);


      const containerRect = mainContainerRef.current.getBoundingClientRect();
      const totalWidth = containerRect.width;


      const finalThumbnailPosition = isMobileView ? thumbnailsPositionMobile : thumbnailsPosition;
      const finalThumbWidth        = isMobileView ? thumbnailWidthMobile : thumbnailWidth;
      const finalThumbHeight       = isMobileView ? thumbnailHeightMobile : thumbnailHeight;

      let thumbnailSpace = 0;
      if (!isMobileView && showThumbnails && (finalThumbnailPosition === 'left' || finalThumbnailPosition === 'right')) {
        thumbnailSpace = finalThumbWidth + 18;
      }


      let buttonSpace = windowWidth < 500 ? windowWidth * 0.16 : 80



      const safetyMargin   = Math.max(10, Math.floor(totalWidth * 0.03));
      const availableWidth = Math.max(100, totalWidth - thumbnailSpace - buttonSpace - safetyMargin);



      let targetItemWidth = itemWidth || maxItemWidth;
      targetItemWidth = Math.min(targetItemWidth, maxItemWidth);
      targetItemWidth = Math.max(targetItemWidth, minItemWidth);
      if (targetItemWidth > availableWidth) targetItemWidth = availableWidth;



      let itemsToShow = Math.max(1, Math.floor(availableWidth / targetItemWidth));



      // ----------- GAP OPTIMISÉ ----------- //
      let minGapPerItem = Math.floor((availableWidth - targetItemWidth * itemsToShow) / itemsToShow)
      /*Math.max(
        14,
        Math.min(
          isMobileView ? 28 : 40,
          Math.floor((availableWidth - targetItemWidth * itemsToShow) / itemsToShow)
        )
      );
*/



      Math.floor(targetItemWidth - minGapPerItem); // Math.floor(slotWidth - minGapPerItem);


      setEffectiveThumbnailPosition(finalThumbnailPosition);
      setEffectiveThumbnailWidth(finalThumbWidth);
      setEffectiveThumbnailHeight(finalThumbHeight);
      setCalculatedItemWidth(targetItemWidth) // (finalItemWidth);
      setVisibleItems(itemsToShow);
      setGap(minGapPerItem / 2);
    };

    const timeoutId = setTimeout(updateLayout, 200);
    window.addEventListener('resize', updateLayout);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateLayout);
    };
  }, [
    itemWidth,
    minItemWidth,
    maxItemWidth,
    items.length,
    thumbnailsPosition,
    thumbnailsPositionMobile,
    showThumbnails,
    thumbnailWidth,
    thumbnailHeight,
    thumbnailWidthMobile,
    thumbnailHeightMobile,
    mobileBreakpoint,
  ]);

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (loop ? (prevIndex - 1 + items.length) % items.length : Math.max(0, prevIndex - 1)));
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (loop ? (prevIndex + 1) % items.length : Math.min(items.length - 1, prevIndex + 1)));
  };

  const canGoPrev = loop || currentIndex > 0;
  const canGoNext = loop || currentIndex < items.length - 1;

  const handleTouchStart = (e: React.TouchEvent) => setTouchStartX(e.touches[0].clientX);
  const handleTouchMove = (e: React.TouchEvent) => setTouchMoveX(e.touches[0].clientX);
  const handleTouchEnd = () => {
    if (touchStartX !== null && touchMoveX !== null) {
      const deltaX = touchStartX - touchMoveX;
      if (deltaX > 50) nextSlide();
      else if (deltaX < -50) prevSlide();
    }
    setTouchStartX(null);
    setTouchMoveX(null);
  };

  const renderThumbnails = () => (
    <div
      className={classNames(
        styles.carouselThumbnails,
        styles[`thumbnails${effectiveThumbnailPosition.charAt(0).toUpperCase() + effectiveThumbnailPosition.slice(1)}`]
      )}
    >
      {items.map((_, index) => (
        <button
          key={index}
          onClick={() => setCurrentIndex(index)}
          className={classNames(styles.carouselThumbnail, { [styles.activeThumbnail]: index === currentIndex })}
          style={{ width: `${effectiveThumbnailWidth}px`, height: `${effectiveThumbnailHeight}px` }}
        >
          <div style={{ width: `${effectiveThumbnailWidth}px`, height: `${effectiveThumbnailHeight}px` }}>
            {thumbnails[index]}
          </div>
        </button>
      ))}
    </div>
  );

  const totalOffset = currentIndex * (calculatedItemWidth + gap * 2);

  const carouselContent = (
    <div className={classNames(styles.carouselInner)}>
      <button
        onClick={prevSlide}
        className={classNames(styles.carouselBtn, styles.prev)}
        style={{ visibility: canGoPrev ? 'visible' : 'hidden' }}
        aria-label="Previous"
      >
        <LeftArrowSVG color={arrowColor} />
      </button>
      <div
        className={classNames(styles.carouselWrapper)}
        ref={wrapperRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={classNames(styles.carouselContent)}
          style={{
            transform: `translateX(-${totalOffset}px)`,
            width: `${items.length * (calculatedItemWidth + gap * 2)}px`,
			height: "auto",
          }}
        >
          {items.map((item, index) => (
            <div
              key={index}
              ref={index === 0 ? firstItemRef : null}
              style={{
                width: `${calculatedItemWidth}px`,
                marginLeft: `${gap}px`,
                marginRight: `${gap}px`,
                flexShrink: 0,
              }}
            >
              <div className={classNames(styles.carouselItem)}>{item}</div>
              {showLegends && legends[index] && (
                <div className={styles.carouselLegend} style={{ color: legendColor, width: `${calculatedItemWidth}px` }}>
                  {legends[index]}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <button
        onClick={nextSlide}
        className={classNames(styles.carouselBtn, styles.next)}
        style={{ visibility: canGoNext ? 'visible' : 'hidden' }}
        aria-label="Next"
      >
        <RightArrowSVG color={arrowColor} />
      </button>
    </div>
  );

  return (
    <div
      className={classNames(styles.carouselMainContainer, {
        [styles.withThumbnailsLeft]: !isMobile && showThumbnails && effectiveThumbnailPosition === 'left',
        [styles.withThumbnailsRight]: !isMobile && showThumbnails && effectiveThumbnailPosition === 'right',
      })}
      ref={mainContainerRef}
    >
      {!isMobile && showThumbnails && effectiveThumbnailPosition === 'left' && renderThumbnails()}
      {!isMobile && showThumbnails && effectiveThumbnailPosition === 'top' && renderThumbnails()}
      <div className={classNames(styles.carouselContainer)}>{carouselContent}</div>
      {!isMobile && showThumbnails && effectiveThumbnailPosition === 'right' && renderThumbnails()}
      {(isMobile || effectiveThumbnailPosition === 'bottom') && showThumbnails && renderThumbnails()}
    </div>
  );
};

export default Carousel2;
